import { useMemo, useState } from "react";
import {
  useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getPaginationRowModel, flexRender,
} from "@tanstack/react-table";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowUpDown, CheckCircle2, PauseCircle, RotateCcw, ChevronLeft, ChevronRight, Search, Eye, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const docUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);
const DocView = ({ label, url }) => {
  if (!url) return <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">{label}: not provided</div>;
  const isPdf = url.toLowerCase().includes(".pdf");
  return (
    <div className="rounded-lg border p-2">
      <p className="text-xs font-medium mb-1.5">{label}</p>
      {isPdf ? (
        <a href={docUrl(url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><FileText className="w-4 h-4" /> Open PDF</a>
      ) : (
        <a href={docUrl(url)} target="_blank" rel="noopener noreferrer"><img src={docUrl(url)} alt={label} className="w-full h-40 object-contain rounded bg-muted" /></a>
      )}
    </div>
  );
};

const statusColor = (s) => ({ approved: "bg-green-600", pending: "bg-amber-500", suspended: "bg-red-500", rejected: "bg-muted-foreground", none: "bg-muted-foreground" }[s] || "bg-muted-foreground");

export const TenantManagement = ({ tenants = [], onChanged }) => {
  const [sorting, setSorting] = useState([{ id: "created_at", desc: true }]);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [docsFor, setDocsFor] = useState(null);

  const bulk = async (action) => {
    const ids = Object.keys(rowSelection).map((i) => filtered[Number(i)]?.user_id).filter(Boolean);
    if (ids.length === 0) return toast.error("Select at least one store");
    try {
      const { data } = await api.post("/admin/sellers/bulk-action", { user_ids: ids, action });
      toast.success(`${data.updated} store(s) ${action}d`);
      setRowSelection({}); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const single = async (uid, action, reason = "") => {
    try {
      await api.post(`/admin/sellers/${uid}/${action}${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`);
      toast.success(action === "approve" ? "Store approved — seller emailed" : action === "reject" ? "Application rejected — seller emailed" : `Store ${action}d`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const rejectSeller = (uid) => {
    const reason = window.prompt("Reason for rejecting this store? (emailed to the seller)");
    if (reason === null) return;
    single(uid, "reject", reason.trim());
  };

  const filtered = useMemo(
    () => tenants.filter((t) => statusFilter === "all" || t.seller_status === statusFilter),
    [tenants, statusFilter]
  );

  const columns = useMemo(() => [
    {
      id: "select",
      header: ({ table }) => <Checkbox checked={table.getIsAllRowsSelected()} onCheckedChange={(v) => table.toggleAllRowsSelected(!!v)} aria-label="Select all" data-testid="tenant-select-all" />,
      cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(!!v)} aria-label="Select row" data-testid={`tenant-select-${row.original.user_id}`} />,
      enableSorting: false,
    },
    { accessorKey: "store_name", header: "Store", cell: ({ row }) => (
      <div><p className="font-medium text-sm">{row.original.store_name}</p><p className="text-xs text-muted-foreground">{row.original.email}</p></div>
    )},
    { accessorKey: "seller_status", header: "Status", cell: ({ getValue }) => <Badge className={statusColor(getValue())}>{getValue()}</Badge> },
    { accessorKey: "product_count", header: "Products" },
    { accessorKey: "order_count", header: "Orders" },
    { accessorKey: "created_at", header: "Joined", cell: ({ getValue }) => <span className="text-xs text-muted-foreground">{getValue() ? new Date(getValue()).toLocaleDateString() : "—"}</span> },
    { id: "actions", header: "", cell: ({ row }) => (
      <div className="flex gap-1 justify-end">
        <Button size="sm" variant="ghost" className="h-8" onClick={() => setDocsFor(row.original)} data-testid={`tenant-docs-${row.original.user_id}`}><Eye className="w-4 h-4" /></Button>
        {row.original.seller_status !== "approved" && <Button size="sm" variant="outline" className="h-8" onClick={() => single(row.original.user_id, "approve")} data-testid={`tenant-approve-${row.original.user_id}`}>Approve</Button>}
        {(row.original.seller_status === "pending") && <Button size="sm" variant="ghost" className="h-8 text-destructive" onClick={() => rejectSeller(row.original.user_id)} data-testid={`tenant-reject-${row.original.user_id}`}>Reject</Button>}
        {row.original.seller_status === "approved" && <Button size="sm" variant="outline" className="h-8" onClick={() => single(row.original.user_id, "suspend")} data-testid={`tenant-suspend-${row.original.user_id}`}>Deactivate</Button>}
      </div>
    )},
  ], []); // eslint-disable-line

  const table = useReactTable({
    data: filtered, columns,
    state: { sorting, rowSelection, globalFilter },
    onSortingChange: setSorting, onRowSelectionChange: setRowSelection, onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(), getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _col, val) => {
      const v = String(val).toLowerCase();
      return (row.original.store_name || "").toLowerCase().includes(v) || (row.original.email || "").toLowerCase().includes(v);
    },
    initialState: { pagination: { pageSize: 10 } },
  });

  const selectedCount = Object.keys(rowSelection).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="tenant-search" value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search stores…" className="pl-9 rounded-full" />
        </div>
        <div className="flex gap-1">
          {["all", "pending", "approved", "suspended"].map((s) => (
            <Button key={s} size="sm" variant={statusFilter === s ? "default" : "outline"} className="rounded-full capitalize h-9" data-testid={`tenant-filter-${s}`} onClick={() => setStatusFilter(s)}>{s}</Button>
          ))}
        </div>
      </div>

      {selectedCount > 0 && (
        <Card className="p-3 flex flex-wrap items-center gap-2 bg-primary/5 border-primary/30" data-testid="tenant-bulk-bar">
          <span className="text-sm font-medium">{selectedCount} selected</span>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" onClick={() => bulk("approve")} data-testid="tenant-bulk-approve"><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>
            <Button size="sm" variant="outline" onClick={() => bulk("suspend")} data-testid="tenant-bulk-suspend"><PauseCircle className="w-4 h-4 mr-1" />Suspend</Button>
            <Button size="sm" variant="outline" onClick={() => bulk("reactivate")} data-testid="tenant-bulk-reactivate"><RotateCcw className="w-4 h-4 mr-1" />Reactivate</Button>
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <TableHead key={h.id} className={h.column.getCanSort() ? "cursor-pointer select-none" : ""} onClick={h.column.getToggleSortingHandler()}>
                    <span className="inline-flex items-center gap-1">{flexRender(h.column.columnDef.header, h.getContext())}{h.column.getCanSort() && h.column.id !== "select" && <ArrowUpDown className="w-3 h-3 opacity-50" />}</span>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} data-testid={`tenant-row-${row.original.user_id}`} data-state={row.getIsSelected() ? "selected" : undefined}>
                {row.getVisibleCells().map((cell) => <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>)}
              </TableRow>
            ))}
            {table.getRowModel().rows.length === 0 && <TableRow><TableCell colSpan={columns.length} className="text-center py-10 text-muted-foreground">No stores match your filters.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{table.getFilteredRowModel().rows.length} stores</p>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-8 w-8" disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-xs">Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() || 1}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Dialog open={!!docsFor} onOpenChange={(o) => !o && setDocsFor(null)}>
        <DialogContent className="max-w-lg" data-testid="tenant-docs-dialog">
          <DialogHeader><DialogTitle>Verification — {docsFor?.business_name || docsFor?.name}</DialogTitle></DialogHeader>
          {docsFor && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <DocView label="Selfie" url={docsFor.selfie_doc} />
                <DocView label="Government ID" url={docsFor.gov_id_doc} />
              </div>
              <DocView label="Bank statement" url={docsFor.bank_statement_doc} />
              {docsFor.bank_details && <p className="text-xs text-muted-foreground">Bank details: {docsFor.bank_details}</p>}
              {docsFor.seller_status !== "approved" && (
                <div className="flex gap-2">
                  <Button className="flex-1 rounded-full" data-testid="tenant-docs-approve" onClick={() => { single(docsFor.user_id, "approve"); setDocsFor(null); }}>
                    <CheckCircle2 className="w-4 h-4 mr-1" />Approve store
                  </Button>
                  <Button variant="outline" className="flex-1 rounded-full text-destructive" data-testid="tenant-docs-reject" onClick={() => { rejectSeller(docsFor.user_id); setDocsFor(null); }}>
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
